// ============================================================================
// v4 — inline SVG data-viz toolkit (glowing gradient charts, no chart lib)
// Deterministic; styled for the dark/futuristic system.
// ============================================================================
import React from 'react';

let _uid = 0;
const uid = (p) => `${p}${++_uid}`;

// Catmull-Rom -> cubic bezier for smooth lines
function smoothPath(pts) {
  if (!pts.length) return '';
  if (pts.length < 2) return `M ${pts[0][0]},${pts[0][1]}`;
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0]},${p2[1]}`;
  }
  return d;
}

function toPoints(data, w, h, pad = 3) {
  const min = Math.min(...data), max = Math.max(...data), rng = (max - min) || 1;
  return data.map((v, i) => [
    data.length === 1 ? w / 2 : (i / (data.length - 1)) * w,
    h - ((v - min) / rng) * (h - pad * 2) - pad,
  ]);
}

// Compact sparkline with optional gradient fill + glow
export function Spark({ data, color = '#5A54F3', w = 132, h = 40, fill = true, glow = true }) {
  const g = uid('sk');
  const pts = toPoints(data, w, h);
  const line = smoothPath(pts);
  const area = fill ? `${line} L ${w},${h} L 0,${h} Z` : '';
  return (
    <svg className="v4-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width={w} height={h}>
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.34" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
        {glow && (
          <filter id={g + 'f'} x="-20%" y="-40%" width="140%" height="180%">
            <feGaussianBlur stdDeviation="1.6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        )}
      </defs>
      {fill && <path d={area} fill={`url(#${g})`} />}
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"
        vectorEffect="non-scaling-stroke" filter={glow ? `url(#${g}f)` : undefined} />
    </svg>
  );
}

// Big dual-series area chart with grid, markers, glow
export function AreaChart({ series, labels, w = 760, h = 300, height }) {
  const H = height || h;
  const padL = 46, padB = 26, padT = 14, padR = 12;
  const iw = w - padL - padR, ih = H - padT - padB;
  const all = series.flatMap(s => s.data);
  const max = Math.max(...all) * 1.12, min = 0;
  const rng = (max - min) || 1;
  const x = (i, n) => padL + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
  const y = (v) => padT + ih - ((v - min) / rng) * ih;
  const gridY = 4;
  return (
    <svg className="v4-area" viewBox={`0 0 ${w} ${H}`} width="100%">
      <defs>
        {series.map((s, si) => (
          <linearGradient key={si} id={`ag${si}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={s.color} stopOpacity="0" />
          </linearGradient>
        ))}
        <filter id="agglow" x="-5%" y="-30%" width="110%" height="160%">
          <feGaussianBlur stdDeviation="1.4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* grid */}
      {Array.from({ length: gridY + 1 }).map((_, i) => {
        const gy = padT + (ih / gridY) * i;
        const val = max - (rng / gridY) * i;
        return (
          <g key={i}>
            <line x1={padL} y1={gy} x2={w - padR} y2={gy} stroke="rgba(255,255,255,.055)" strokeWidth="1" />
            <text x={padL - 8} y={gy + 3} textAnchor="end" className="v4-axis">{fmtAxis(val)}</text>
          </g>
        );
      })}
      {/* x labels (sparse) */}
      {labels && labels.map((lb, i) => (
        (i % Math.ceil(labels.length / 6) === 0) &&
        <text key={i} x={x(i, labels.length)} y={H - 8} textAnchor="middle" className="v4-axis">{lb}</text>
      ))}
      {/* series */}
      {series.map((s, si) => {
        const pts = s.data.map((v, i) => [x(i, s.data.length), y(v)]);
        const line = smoothPath(pts);
        const area = `${line} L ${x(s.data.length - 1, s.data.length)},${padT + ih} L ${x(0, s.data.length)},${padT + ih} Z`;
        const lastPt = pts[pts.length - 1];
        return (
          <g key={si}>
            {s.fill !== false && <path d={area} fill={`url(#ag${si})`} />}
            <path d={line} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round"
              vectorEffect="non-scaling-stroke" filter="url(#agglow)" />
            <circle cx={lastPt[0]} cy={lastPt[1]} r="2.4" fill="#0a0a12" stroke={s.color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
          </g>
        );
      })}
    </svg>
  );
}

// SVG donut with center label
export function Donut({ segments, size = 176, thickness = 22, center }) {
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  let acc = 0;
  return (
    <svg className="v4-donut" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth={thickness} />
      {segments.map((s, i) => {
        const frac = s.value / total;
        const dash = frac * circ;
        const el = (
          <circle key={i} cx={c} cy={c} r={r} fill="none" stroke={s.color} strokeWidth={thickness}
            strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-acc * circ}
            transform={`rotate(-90 ${c} ${c})`} strokeLinecap="butt" />
        );
        acc += frac;
        return el;
      })}
      {center && (
        <g>
          <text x={c} y={c - 2} textAnchor="middle" className="v4-donut-val">{center.value}</text>
          <text x={c} y={c + 18} textAnchor="middle" className="v4-donut-lbl">{center.label}</text>
        </g>
      )}
    </svg>
  );
}

function fmtAxis(v) {
  if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return '$' + Math.round(v / 1000) + 'k';
  return '$' + Math.round(v);
}

// Vertical bars with optional goal line + per-bar color
export function Bars({ data, goal, w = 720, h = 200, colorFor, fade = true }) {
  const padB = 4, padT = 10;
  const max = Math.max(...data, goal || 0) * 1.1 || 1;
  const n = data.length;
  const gap = w / n * 0.32;
  const bw = w / n - gap;
  const y = (v) => padT + (h - padT - padB) - (v / max) * (h - padT - padB);
  const gy = goal ? y(goal) : null;
  const u = uid('bars');
  const bars = data.map((v, i) => ({ v, i, col: colorFor ? colorFor(v, i) : '#5A54F3' }));
  const colors = [...new Set(bars.map((b) => b.col))];
  const gid = (c) => `${u}${c.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <svg className="v4-bars" viewBox={`0 0 ${w} ${h}`} width="100%" preserveAspectRatio="none">
      <defs>
        {colors.map((c) => (
          <linearGradient key={c} id={gid(c)} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c} stopOpacity="1" />
            <stop offset="28%" stopColor={c} stopOpacity="0.38" />
            <stop offset="100%" stopColor={c} stopOpacity="0.02" />
          </linearGradient>
        ))}
      </defs>
      {bars.map(({ v, i, col }) => {
        const x = (w / n) * i + gap / 2;
        const bh = (h - padT - padB) - (y(v) - padT);
        return <rect key={i} x={x} y={y(v)} width={bw} height={Math.max(2, bh)} rx="3" fill={fade ? `url(#${gid(col)})` : col} />;
      })}
      {gy != null && <line x1="0" y1={gy} x2={w} y2={gy} stroke="rgba(255,255,255,.35)" strokeWidth="1.4" strokeDasharray="5 5" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}

// Circular progress gauge
export function Ring({ pct, size = 150, thickness = 12, color = '#5A54F3', label, sub }) {
  const r = (size - thickness) / 2, c = size / 2, circ = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(1, pct / 100));
  const g = uid('rg');
  return (
    <svg className="v4-ring" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#5A54F3" /><stop offset="100%" stopColor="#8AE8BD" />
        </linearGradient>
      </defs>
      <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth={thickness} />
      <circle cx={c} cy={c} r={r} fill="none" stroke={`url(#${g})`} strokeWidth={thickness} strokeLinecap="round"
        strokeDasharray={`${p * circ} ${circ}`} transform={`rotate(-90 ${c} ${c})`}
        style={{ filter: 'drop-shadow(0 0 6px rgba(90,84,243,.5))' }} />
      {label && <text x={c} y={c - 2} textAnchor="middle" className="v4-ring-val">{label}</text>}
      {sub && <text x={c} y={c + 18} textAnchor="middle" className="v4-ring-sub">{sub}</text>}
    </svg>
  );
}

// Horizontal labeled bar (for "top channels" style lists)
export function HBar({ value, max, color = '#5A54F3', w = 160 }) {
  const p = Math.max(0.02, Math.min(1, value / (max || 1)));
  return (
    <span className="v4-hbar" style={{ width: w }}>
      <span className="v4-hbar-fill" style={{ width: `${p * 100}%`, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
    </span>
  );
}
